import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import OutsideClickHandler from 'react-outside-click-handler'
import useAuth from '../../../auth/useAuth'

import LoaderComponent from '../../CommonComponents/LoaderComponent'
import { toast } from '../../CommonComponents/ToastComponent/toast'

import TemplateEditorHeader from '../../RuleManagerComponents/TemplateEditorHeader'
import RuleSection from '../RuleSection'
import TemplateEditorFooter from '../../RuleManagerComponents/TemplateEditorFooter'

import {
  hideRuleManager,
  getTemplate,
  deleteTemplate,
} from '../../../redux/actions/ruleManager'
import {
  saveEpTemplate,
  updateEpTemplate,
} from '../../../redux/actions/eventPlanning'
import { selectCurrentAccount } from '../../../redux/reducers/header'
import { RULE_TYPE_EP } from '../../../utils/defaultValues'
import { ignoreOutsideClick } from '../../../services/helper'

const TemplateEditor = () => {
  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth()

  const currentAccount = useSelector(selectCurrentAccount)
  const templateToEdit = useSelector(state => state.ruleManager.templateToEdit)

  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState(
    currentAccount?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  )
  const [slots, setSlots] = useState([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!templateToEdit) {
      setName('')
      setTimezone(
        currentAccount?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      )
      setSlots([])

      return
    }

    // It means the template ID is passed, not the whole object,
    // so we need to retrieve template details.
    if (typeof templateToEdit !== 'object') {
      (async () => {
        const accessToken = await getAccessTokenSilently()
        setIsSaving(true)
        await dispatch(getTemplate(accessToken, RULE_TYPE_EP, templateToEdit))
        setIsSaving(false)
      })()
      return
    }

    setName(templateToEdit.name)
    setTimezone(templateToEdit.timezone)
    setSlots(templateToEdit.slots.map((slot, index) => (
      Object.assign({}, slot, {
        id: `${Math.floor(Math.random() * 1000).toString()}-${index}`,
        r: slot.r.map((rule, ruleIndex) => (
          Object.assign({}, rule, {
            id: `${Math.floor(Math.random() * 1000).toString()}-${ruleIndex}`,
          })
        ))
      })
    )))
  }, [templateToEdit]) // eslint-disable-line

  const isSavable = useMemo(() => {
    return slots.filter(slot => slot.s && slot.e).length > 0
  }, [slots])

  // Hide pane.
  const handleClose = () => {
    dispatch(hideRuleManager())
  }

  const handleOutsideClick = (event) => {
    if (!ignoreOutsideClick(event)) {
      handleClose()
    }
  }

  const stripIds = (slotsToSave) => {
    return slotsToSave.map((slot) => {
      const slotWithoutId = Object.assign({}, slot, {
        r: slot.r.map((rule) => {
          const ruleWithoutId = Object.assign({}, rule)
          delete ruleWithoutId.id
          return ruleWithoutId
        }),
      })
      delete slotWithoutId.id
      return slotWithoutId
    })
  }

  const handleSave = async () => {
    const accessToken = await getAccessTokenSilently()
    if (templateToEdit) {
      setIsSaving(true)
      await dispatch(updateEpTemplate(
        accessToken,
        templateToEdit.id,
        name,
        timezone,
        stripIds(slots),
      ))
      setIsSaving(false)
    } else {
      if (name === '') {
        toast.show({
          title: 'Warning',
          description: 'Please enter a template name.',
        })
        return
      }

      setIsSaving(true)
      await dispatch(saveEpTemplate(
        accessToken,
        name,
        timezone,
        stripIds(slots),
      ))
      setIsSaving(false)
    }
  }

  const handleSaveAsAnother = async () => {
    if (templateToEdit && templateToEdit.name === name) {
      toast.show({
        title: 'Warning',
        description: 'Please enter a name different than the original one.',
      })
      return
    }

    setIsSaving(true)
    const accessToken = await getAccessTokenSilently()
    await dispatch(saveEpTemplate(
      accessToken,
      name,
      timezone,
      stripIds(slots),
    ))
    setIsSaving(false)
  }

  const handleDelete = async () => {
    const accessToken = await getAccessTokenSilently()
    setIsSaving(true)
    await dispatch(deleteTemplate(accessToken, RULE_TYPE_EP, templateToEdit.id))
    setIsSaving(false)
    handleClose()
  }

  const renderBody = () => {
    // We pass an empty array by storing it first
    // as a variable. If we pass [] directly to props,
    // a new object ([]) will be created on every render,
    // causing a maximum depth reached exception.
    const currentTemplates = []

    return (
      <div className="pane-body">
        <RuleSection
          hasSpCampaign
          slots={slots}
          currentTemplates={currentTemplates}
          timezone={timezone}
          onChange={setSlots}
        />
      </div>
    )
  }

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <div className="event-planning-template-editor-component">
        { isSaving && <LoaderComponent /> }
        <TemplateEditorHeader
          templateType="Event Planning/Seasonality"
          name={name}
          timezone={timezone}
          onNameChange={setName}
          onTimezoneChange={setTimezone}
          onClose={handleClose}
        />
        { renderBody() }
        <TemplateEditorFooter
          isSaving={isSaving}
          isSavable={isSavable}
          isEditable={templateToEdit !== null}
          onSave={handleSave}
          onSaveAsAnother={handleSaveAsAnother}
          onDelete={handleDelete}
          onClose={handleClose}
        />
      </div>
    </OutsideClickHandler>
  )
}

export default TemplateEditor
